<script setup>
import moment from 'moment'
import { UserRoundMinus } from 'lucide-vue-next'
import ChatAvatar from '@/pages/Project/Chat/ChatAvatar.vue'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

defineProps({
  joined: { type: Array, default: () => [] },
  size: { type: String, default: 'sm' }, // 'xs' | 'sm'
  removable: { type: Boolean, default: false },
})
const emit = defineEmits(['remove'])

const canRemove = (member) => !!member.project_member_id && !member.is_self
</script>

<template>
  <Popover v-if="joined.length">
    <PopoverTrigger as-child>
      <button
        type="button"
        class="flex shrink-0 items-center whitespace-nowrap rounded-full py-0.5 pl-0.5 pr-2 transition-colors hover:bg-muted"
        title="See who joined via this link"
      >
        <div class="flex items-center">
          <ChatAvatar
            v-for="(member, i) in joined.slice(0, 3)"
            :key="member.member_id"
            :author="member"
            :size="size"
            class="ring-2 ring-background"
            :class="i > 0 ? (size === 'xs' ? '-ml-1.5' : '-ml-2') : ''"
          />
        </div>
        <span class="ml-1.5 font-medium text-primary" :class="size === 'xs' ? 'text-xs' : 'text-sm'">
          {{ joined.length }} joined
        </span>
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" class="w-80 p-2">
      <p class="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Joined via this link</p>
      <ul class="max-h-64 overflow-y-auto">
        <li
          v-for="member in joined"
          :key="member.member_id"
          class="group flex items-center gap-2 rounded-md px-2 py-1.5"
          data-testid="joined-member"
        >
          <ChatAvatar :author="member" size="base" />
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-medium">{{ member.display_name }}</p>
            <p class="text-xs text-muted-foreground">Joined {{ moment(member.joined_at).format('D MMM YYYY') }}</p>
          </div>
          <Button
            v-if="removable && canRemove(member)"
            type="button"
            variant="ghost"
            size="sm"
            class="h-7 shrink-0 px-2 text-xs text-muted-foreground hover:text-destructive"
            title="Remove from project"
            data-testid="remove-joined-member"
            @click="emit('remove', member)"
          >
            <UserRoundMinus class="mr-1 size-4" />
            Remove
          </Button>
        </li>
      </ul>
    </PopoverContent>
  </Popover>
  <span v-else class="whitespace-nowrap text-muted-foreground" :class="size === 'xs' ? 'text-xs' : 'text-sm'">No one joined yet</span>
</template>
